import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Download, ExternalLink, FileText, Printer, ShieldCheck } from 'lucide-react';

type LegalStatus = 'Ready for review' | 'Needs company details' | 'Requires signed contract' | 'Operational policy';

interface LegalDocument {
  id: string;
  title: string;
  status: LegalStatus;
  owner: string;
  reviewCycle: string;
  purpose: string;
  requiredActions: string[];
  sections: string[];
}

const officialReferences = [
  {
    label: 'ICO DPIA guidance',
    href: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/data-protection-impact-assessments-dpias/',
  },
  {
    label: 'ICO records of processing and lawful basis',
    href: 'https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-of-processing-and-lawful-basis/',
  },
  {
    label: 'ICO controller and processor contracts',
    href: 'https://ico.org.uk/media/for-organisations/guide-to-the-general-data-protection-regulation-gdpr/accountability-and-governance/contracts-1-0.pdf',
  },
  {
    label: 'GOV.UK cookie consent pattern',
    href: 'https://design-system.service.gov.uk/patterns/cookies-page/',
  },
];

const legalDocuments: LegalDocument[] = [
  {
    id: 'terms',
    title: 'Terms and Conditions',
    status: 'Needs company details',
    owner: 'System Owner / Registered Manager',
    reviewCycle: 'Annual or before commercial launch',
    purpose: 'Defines permitted use, account responsibilities, acceptable use, intellectual property, service availability and limitations.',
    requiredActions: [
      'Insert final company legal entity details and trading name.',
      'Confirm commercial terms, support hours and liability wording with a solicitor.',
      'Publish the final approved version before onboarding external customers.',
    ],
    sections: [
      'These Terms govern access to the Fuchsia Home Care software platform and associated administrator, carer and family portals.',
      'Users must only access information required for their role and must not share credentials or export data without authority.',
      'The platform is provided for care operations, scheduling, reporting, payroll, billing, document management and compliance evidence.',
      'All intellectual property in the platform, configuration, workflows and documentation remains proprietary to Dorobat Marius-Dorinel unless a separate written licence states otherwise.',
      'Operational decisions, clinical decisions and safeguarding decisions require human review and approval by an authorised person.',
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    status: 'Ready for review',
    owner: 'Registered Manager / Data Protection Lead',
    reviewCycle: 'Annual and after any new processing activity',
    purpose: 'Explains how personal data is collected, used, retained, shared, secured and how data subject rights can be exercised.',
    requiredActions: [
      'Confirm controller identity, ICO registration details and contact email.',
      'Map all processors used in production, including hosting, email, SMS, AI and storage.',
      'Publish the final notice in plain English for staff, clients, carers and family users.',
    ],
    sections: [
      'The application processes client, family, carer, staff, rota, visit, payroll, billing, medication, incident, safeguarding and compliance information.',
      'The main purposes are care delivery, safe staffing, legal and regulatory compliance, financial administration, safeguarding, audit and service improvement.',
      'Special category health and care information is processed only where necessary for safe care delivery and compliance with health and social care obligations.',
      'Access is role-based and branch-aware, with audit logging for sensitive activity and document access.',
      'Data subjects may request access, correction, restriction, deletion where applicable, portability and objection, subject to lawful exemptions.',
    ],
  },
  {
    id: 'dpa',
    title: 'Data Processing Agreement',
    status: 'Requires signed contract',
    owner: 'System Owner / Supplier Manager',
    reviewCycle: 'Before each processor goes live',
    purpose: 'Sets Article 28 style obligations between the controller and processors or sub-processors.',
    requiredActions: [
      'Identify each processor and sub-processor used in production.',
      'Sign processor terms covering instructions, confidentiality, security, sub-processing, assistance, deletion and audits.',
      'Record international transfer safeguards where any data leaves the UK or adequate jurisdictions.',
    ],
    sections: [
      'The processor must process personal data only on documented instructions from the controller.',
      'The processor must ensure confidentiality, appropriate technical and organisational measures, breach notification support and deletion or return of data at termination.',
      'The processor may not appoint sub-processors without prior authorisation and equivalent contractual protections.',
      'The controller must maintain a processor register including purpose, data categories, location, security measures and contract status.',
    ],
  },
  {
    id: 'cookie',
    title: 'Cookie Policy',
    status: 'Needs company details',
    owner: 'Administrator / Web Owner',
    reviewCycle: 'Whenever cookies or analytics change',
    purpose: 'Explains essential cookies and any non-essential cookies requiring consent.',
    requiredActions: [
      'Confirm every cookie used in production, including authentication, preferences, analytics and third-party tools.',
      'Keep strictly necessary cookies separate from optional analytics or marketing cookies.',
      'Add a cookie banner and preference control before enabling any non-essential cookies.',
    ],
    sections: [
      'Strictly necessary cookies support login sessions, security, language preference, dark mode preference and application stability.',
      'Non-essential analytics, marketing or tracking cookies must be off unless the user has given consent.',
      'The policy must list cookie name, purpose, provider, duration and whether it is essential.',
      'Users must be able to change or withdraw non-essential cookie consent.',
    ],
  },
  {
    id: 'dpia',
    title: 'Data Protection Impact Assessment',
    status: 'Ready for review',
    owner: 'Data Protection Lead / Registered Manager',
    reviewCycle: 'Before launch and before high-risk changes',
    purpose: 'Assesses privacy risks for health, location, payroll, AI, family access and document processing.',
    requiredActions: [
      'Complete risk scoring with real production architecture and processors.',
      'Assess GPS tracking, AI assistance, eMAR, family portal, document storage and payroll processing.',
      'Consult the ICO before processing only if high risks remain unmitigated.',
    ],
    sections: [
      'Processing includes special category care information, live carer location, medication records, payroll data, family updates and compliance evidence.',
      'Necessity is linked to safe care delivery, auditability, workforce management, legal obligations and safeguarding.',
      'Key risks include excessive access, inaccurate records, inappropriate family disclosure, location misuse, AI over-reliance and processor breach.',
      'Mitigations include role-based access, branch restrictions, audit logs, encryption, human approval, server-side API keys, data minimisation and review cycles.',
    ],
  },
  {
    id: 'ropa',
    title: 'Record of Processing Activities',
    status: 'Ready for review',
    owner: 'Data Protection Lead',
    reviewCycle: 'Quarterly and after new modules/processors',
    purpose: 'Documents processing activities, lawful basis, data categories, recipients, retention and security controls.',
    requiredActions: [
      'Confirm lawful basis for each activity with the controller.',
      'Add final retention periods, recipients and storage locations.',
      'Keep the ROPA available for audit or regulator request.',
    ],
    sections: [
      'Care delivery: client demographics, care plans, visit notes, medication support, incidents and safeguarding records.',
      'Workforce management: carer profiles, rota, check-in/check-out, GPS evidence, training, DBS/document expiry and payroll.',
      'Finance: invoices, payments, payroll, mileage, cost reporting and accounting exports.',
      'Compliance: audits, complaints, PIR drafts, CQC evidence, policy reviews and legal documentation.',
      'Family engagement: permitted visit updates, messages, acknowledgements and access controls.',
    ],
  },
  {
    id: 'backup',
    title: 'Backup and Restore Policy',
    status: 'Operational policy',
    owner: 'System Owner / Technical Owner',
    reviewCycle: 'Monthly restore test and annual policy review',
    purpose: 'Defines backup frequency, encryption, restore testing, retention and incident recovery responsibilities.',
    requiredActions: [
      'Configure automated encrypted server backups.',
      'Run and record a restore test before production launch.',
      'Document retention, access controls and recovery time objectives.',
    ],
    sections: [
      'Production data must be backed up using encrypted storage with access restricted to authorised technical administrators.',
      'Backups must include application database, encrypted document objects, API key configuration metadata and deployment configuration.',
      'Restore tests must be documented with date, tester, result, recovery time and any corrective actions.',
      'Backup access and restore activity must be logged as security-relevant activity.',
    ],
  },
  {
    id: 'incident-response',
    title: 'Incident Response Policy',
    status: 'Operational policy',
    owner: 'Registered Manager / Security Lead',
    reviewCycle: 'After every incident and annually',
    purpose: 'Defines how security, data protection, service availability and safeguarding incidents are identified, triaged, contained and reported.',
    requiredActions: [
      'Assign named incident roles and escalation contacts.',
      'Link security incidents with safeguarding and CQC reporting routes where relevant.',
      'Define ICO breach assessment and notification workflow.',
    ],
    sections: [
      'Incidents include suspected data breach, unauthorised access, lost device, processor breach, service outage, incorrect disclosure, medication safety event and safeguarding concern.',
      'Immediate steps are identify, contain, preserve evidence, assess risk to individuals, notify internal leads and record actions.',
      'The organisation must assess whether ICO, CQC, affected individuals, police, NHS partners or processors need notification.',
      'A post-incident review must record root cause, remediation, policy updates and staff learning.',
    ],
  },
  {
    id: 'processor-contracts',
    title: 'Processor Contracts Register',
    status: 'Requires signed contract',
    owner: 'System Owner / Supplier Manager',
    reviewCycle: 'Before onboarding and annually',
    purpose: 'Tracks contracts with hosting, email, SMS, AI, maps, accounting, calendar and cloud storage processors.',
    requiredActions: [
      'Create one register row per processor and sub-processor.',
      'Attach signed DPA, security evidence, location details, retention terms and breach notification SLA.',
      'Disable production integration until contract and security review are complete.',
    ],
    sections: [
      'Processor records must include supplier name, service, data categories, processing purpose, storage region, sub-processors, contract date and review date.',
      'Production processors expected for this application may include VPS hosting, email delivery, SMS provider, OpenAI backend, maps provider, accounting provider, calendar provider and encrypted object storage.',
      'No sensitive production integration should be enabled until the processor contract and security review are approved.',
    ],
  },
];

function pdfEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)').slice(0, 150);
}

function wrapLine(value: string, width = 92): string[] {
  const words = value.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > width && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function buildPdfBlob(title: string, lines: string[]): Blob {
  const wrapped = lines.flatMap((line) => (line.trim() ? wrapLine(line) : ['']));
  const commands = ['BT', '/F1 16 Tf', '50 790 Td', `(${pdfEscape(title)}) Tj`, '/F1 9 Tf', '0 -24 Td'];
  wrapped.slice(0, 54).forEach((line, index) => {
    commands.push(`${index === 0 ? '' : 'T* '}(${pdfEscape(line)}) Tj`);
  });
  commands.push('ET');
  const content = commands.join('\n');
  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj',
    '4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj',
    `5 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

function documentLines(documentItem: LegalDocument): string[] {
  return [
    'Fuchsia Home Care - Production and Legal Readiness Document',
    `Document: ${documentItem.title}`,
    `Owner: ${documentItem.owner}`,
    `Status: ${documentItem.status}`,
    `Review cycle: ${documentItem.reviewCycle}`,
    '',
    'Purpose',
    documentItem.purpose,
    '',
    'Required completion actions',
    ...documentItem.requiredActions.map((item, index) => `${index + 1}. ${item}`),
    '',
    'Draft content',
    ...documentItem.sections.map((item, index) => `${index + 1}. ${item}`),
    '',
    'Important note',
    'This document is a production-readiness template and should be reviewed by the organisation, data protection lead and legal adviser before external publication or customer onboarding.',
  ];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function statusClass(status: LegalStatus) {
  if (status === 'Ready for review') return 'bg-emerald-100 text-emerald-800';
  if (status === 'Operational policy') return 'bg-blue-100 text-blue-800';
  if (status === 'Requires signed contract') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

export const ProductionLegal: React.FC = () => {
  const [selectedId, setSelectedId] = useState(legalDocuments[0].id);
  const selectedDocument = legalDocuments.find((documentItem) => documentItem.id === selectedId) || legalDocuments[0];

  const summary = useMemo(() => ({
    ready: legalDocuments.filter((item) => item.status === 'Ready for review').length,
    actions: legalDocuments.reduce((sum, item) => sum + item.requiredActions.length, 0),
    contracts: legalDocuments.filter((item) => item.status === 'Requires signed contract').length,
  }), []);

  const downloadDocument = (documentItem: LegalDocument) => {
    downloadBlob(
      buildPdfBlob(documentItem.title, documentLines(documentItem)),
      `${documentItem.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`
    );
    toast.success(`${documentItem.title} PDF downloaded`);
  };

  const printDocument = (documentItem: LegalDocument) => {
    const url = URL.createObjectURL(buildPdfBlob(documentItem.title, documentLines(documentItem)));
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      toast.error('Allow pop-ups to print the PDF');
      return;
    }
    window.setTimeout(() => {
      popup.print();
      URL.revokeObjectURL(url);
    }, 600);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-lg bg-purple-100 text-purple-700">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-purple-800">Production and legal</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Legal Readiness Centre</h1>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Central register for launch documents, UK GDPR accountability records, processor contracts and operational security policies.
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <p>
                These are implementation templates for production readiness. Final publication and customer onboarding should be approved by the business owner, data protection lead and legal adviser.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Documents</p>
          <p className="mt-3 text-3xl font-bold text-slate-950">{legalDocuments.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Ready for review</p>
          <p className="mt-3 text-3xl font-bold text-emerald-700">{summary.ready}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Required actions</p>
          <p className="mt-3 text-3xl font-bold text-purple-800">{summary.actions}</p>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Document register</h2>
          <div className="mt-4 space-y-3">
            {legalDocuments.map((documentItem) => (
              <button
                key={documentItem.id}
                type="button"
                onClick={() => setSelectedId(documentItem.id)}
                className={`w-full rounded-lg border p-4 text-left transition ${
                  selectedId === documentItem.id
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-slate-950">{documentItem.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{documentItem.owner}</p>
                  </div>
                  <FileText className="h-5 w-5 text-purple-700" />
                </div>
                <span className={`mt-3 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClass(documentItem.status)}`}>
                  {documentItem.status}
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClass(selectedDocument.status)}`}>
                {selectedDocument.status}
              </span>
              <h2 className="mt-3 text-2xl font-bold text-slate-950">{selectedDocument.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{selectedDocument.purpose}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => downloadDocument(selectedDocument)}
                className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
              <button
                type="button"
                onClick={() => printDocument(selectedDocument)}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Printer className="h-4 w-4" />
                Print PDF
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Owner</p>
              <p className="mt-1 font-semibold text-slate-950">{selectedDocument.owner}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Review cycle</p>
              <p className="mt-1 font-semibold text-slate-950">{selectedDocument.reviewCycle}</p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-bold text-slate-950">Required completion actions</h3>
            <div className="mt-3 space-y-2">
              {selectedDocument.requiredActions.map((action) => (
                <div key={action} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{action}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-bold text-slate-950">Draft content</h3>
            <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
              {selectedDocument.sections.map((section, index) => (
                <p key={section}>
                  <span className="font-semibold text-slate-950">{index + 1}. </span>
                  {section}
                </p>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Official reference points</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {officialReferences.map((reference) => (
            <a
              key={reference.href}
              href={reference.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm font-semibold text-purple-800 hover:bg-purple-50"
            >
              {reference.label}
              <ExternalLink className="h-4 w-4" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};
